import React from 'react';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group'
import cx from 'classnames';

import Portal from '../Portal';
import Button from '../Button';
import Heading from '../Heading';
import { IconClose } from '../icons/Icons';

import scopeTab from '../../../helpers/scopeTab';

const TIMEOUT = 250;

export default class Drawer extends React.PureComponent {
    static propTypes = {
        /** Sets the visual appearance of the drawer */
        appearance: PropTypes.oneOf(['light', 'dark']),

        /** Contents of the drawer */
        children: PropTypes.node.isRequired,

        /** Adds a new class to the existing "sarsa-drawer-inner" class */
        className: PropTypes.string,

        /** If the drawer slides in from the left or right side of the screen */
        direction: PropTypes.oneOf(['left', 'right']),

        /** If true, renders an overlay between the page body and the drawer,
         * and the drawer behaves like a modal */
        hasOverlay: PropTypes.bool,

        /** If the drawer is open or not */
        isOpen: PropTypes.bool,

        /** If true, the label is visually displayed as a heading at the top
         * of the drawer */
        isLabelVisible: PropTypes.bool,

        /** Used to describe the drawer to screen reader users. Value is announced
         * after the drawer is opened  */
        label: PropTypes.string.isRequired,

        /** Function called when user clicks the overlay or the close button */
        onClose: PropTypes.func.isRequired,

        /** Function called when drawer has finished closing */
        onCloseComplete: PropTypes.func,

        /** If the drawer should be removed from the DOM after close */
        shouldUnmountOnExit: PropTypes.bool,

        /** The id of the button that opens the trigger. This is required so
         * that focus can be put back on this trigger button once the drawer
         * is closed */
        triggerId: PropTypes.string.isRequired,

        /** The visual width of the drawer */
        width: PropTypes.oneOf(['narrow', 'wide', 'full'])
    };

    static defaultProps = {
        appearance: 'light',
        className: undefined,
        direction: 'left',
        isOpen: false,
        width: 'narrow',
        onCloseComplete: undefined,
        shouldUnmountOnExit: true,
        hasOverlay: true
    };

    constructor(props) {
        super(props);

        this.ref = React.createRef();
        this.recApp = document.getElementById('rec-app') || document.getElementById('recApp');
    }

    componentDidMount() {
        window.addEventListener('keydown', this.handleKeyDown);
    }

    componentDidUpdate(prevProps) {
        const { isOpen, hasOverlay } = this.props;

        if (isOpen && isOpen !== prevProps.isOpen) {
            this.timeout = setTimeout(() => {
                this.ref.current.focus();

                if (hasOverlay) {
                    this.recApp.setAttribute('aria-hidden', 'true');
                    document.body.classList.add('sarsa-drawer-open');
                }
            }, TIMEOUT);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timeout);
        window.removeEventListener('keydown', this.handleKeyDown);
    }

    handleClose = () => {
        const { onClose, triggerId, onCloseComplete } = this.props;

        if (onClose) {
            onClose();

            this.timeout = setTimeout(() => {
                const triggerEl = document.getElementById(triggerId);

                if (triggerEl && triggerEl.focus) {
                    triggerEl.focus();
                }

                if (onCloseComplete) {
                    onCloseComplete();
                }
            }, TIMEOUT);
        }

        this.recApp.removeAttribute('aria-hidden');
        document.body.classList.remove('sarsa-drawer-open');
    }

    handleKeyDown = (event) => {
        const { isOpen, hasOverlay } = this.props;

        if (event.key === 'Escape' && isOpen) {
            event.preventDefault();
            this.handleClose();
        }

        // TODO: Probably would be better to trap the focus inside the drawer like a modal
        if (event.key === 'Tab' && hasOverlay && isOpen) {
            scopeTab(this.ref.current, event);
        }
    }

    render() {
        const {
            isOpen,
            children,
            width,
            direction,
            label,
            isLabelVisible,
            className,
            appearance,
            shouldUnmountOnExit,
            hasOverlay
        } = this.props;

        const isDark = appearance === 'dark';

        // TODO - Overlay could be it's own component and used for modals or
        // other modal-like stuff
        return (
            <Portal>
                {hasOverlay && (
                    <CSSTransition
                        in={this.props.isOpen}
                        timeout={{ appear: 0, enter: 0, exit: 200 }}
                        classNames="sarsa-drawer-overlay"
                        mountOnEnter
                        unmountOnExit={this.props.shouldUnmountOnExit}>
                        <div
                            data-testid="overlay"
                            data-component="Overlay"
                            className={cx('sarsa-drawer-overlay', {
                                'is-hidden': !this.props.isOpen
                            })}
                            onClick={this.handleClose}
                            role="presentation" />
                    </CSSTransition>
                )}
                <CSSTransition
                    in={isOpen}
                    timeout={{ appear: 0, enter: 0, exit: 200 }}
                    classNames="sarsa-drawer"
                    mountOnEnter
                    unmountOnExit={shouldUnmountOnExit}>
                    <div
                        data-component="Drawer"
                        tabIndex="-1"
                        aria-label={label}
                        role="group"
                        ref={this.ref}
                        className={cx('sarsa-drawer', {
                            'direction-left': direction === 'left',
                            'direction-right': direction === 'right',
                            'width-narrow': width === 'narrow',
                            'width-wide': width === 'wide',
                            'width-full': width === 'full'
                        })}>
                        <div
                            className={cx('sarsa-drawer-inner', className, {
                                'sarsa-drawer-dark': isDark
                            })}>
                            <div
                                data-component="DrawerTop"
                                className={cx('sarsa-drawer-top', {
                                    'has-heading': isLabelVisible
                                })}>
                                <div>
                                    {isLabelVisible && (
                                        <Heading
                                            data-testid="heading"
                                            headingLevel={1}
                                            appearance="h5">
                                            {label}
                                        </Heading>
                                    )}
                                </div>

                                <Button
                                    data-testid="close-button"
                                    size="sm"
                                    appearance={isDark ? 'icon-white' : 'subtle'}
                                    iconBeforeElement={<IconClose />}
                                    screenReaderTextAfter={`Close ${label}`}
                                    onClick={this.handleClose} />
                            </div>
                            <div
                                data-component="DrawerContent"
                                className="sarsa-drawer-content">
                                {children}
                            </div>
                        </div>
                    </div>
                </CSSTransition>
            </Portal>
        );
    }
}

